/* eslint-disable */
import axios, { AxiosError, AxiosResponse } from "axios";
import Swal from "sweetalert2";
import querystring from "query-string";
import LocalDataService from "@/service/LocalDataService";
import ErrorMessageUtil from "@/utils/ErrorMessageUtil";

const baseUrl = process.env.VUE_APP_API_URL;
const xkey = process.env.VUE_APP_X_API_KEY;
const client = axios.create();

// レスポンスの型定義を整理
interface CustomResponse extends AxiosResponse {
  data: {
    error?: string;
    status?: string;
    [key: string]: any;
  };
}

const getHeaders = async () => {
  const token = LocalDataService.getAccessToken();
  return {
    Authorization: `${token}`,
    "Content-Type": "application/json; charset=utf-8",
    "x-api-key": xkey,
  };
};

export enum ErrorType {
  UNAUTHORIZED = "unauthorized",
  FIRSTLOGIN = "firstLogin",
}

client.interceptors.response.use(
  (response) => {
    const r = response as CustomResponse;
    
    // URLがある場合（標準的なリクエスト）
    if (r.config.url) {
      if (r.status !== 200 && r.status !== 201) {
        return Promise.reject(r);
      }
      
      // アプリケーション固有のエラーコード (E003以外) がある場合
      if (r.data && r.data.error && r.data.error !== "E003") {
        const errorMessage = ErrorMessageUtil.getErrorMessage(r.data.error);
        console.warn("API Application Error:", r.data.error);
        alert(`${r.data.error}\n${errorMessage}`);
        return Promise.reject(r);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      const responseData = error.response.data as any;
      const message = responseData?.message || "エラーが発生しました";

      if (status >= 500 || status === 400) {
        Swal.fire(String(status), message, "error");
      } else if (status === 401) {
        if (window.location.pathname !== "/logout") {
          LocalDataService.clearAccessToken();
          window.location.href = "/logout";
          return Promise.reject(ErrorType.UNAUTHORIZED);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default class ApiClient {
  public static async get(endpoint: string, params: { [key: string]: any } | null) {
    const url = params 
      ? `${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true, skipEmptyString: true })}`
      : `${baseUrl}${endpoint}`;

    try {
      const response = await client.get(url, {
        headers: await getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      // 安全に error.data を参照する
      throw error.response?.data || error;
    }
  }

  public static async post(endpoint: string, params: any, isAuth = true) {
    const token = LocalDataService.getAccessToken();
    const headers: any = isAuth ? {
      "Content-Type": "application/json",
      "X-API-KEY": xkey || "",
      Authorization: `${token}`,
    } : { "Content-Type": "application/json" };

    try {
      const response = await client.post(`${baseUrl}${endpoint}`, params, { headers });
      return response.data;
    } catch (error: any) {
      console.warn(error);
      throw error.response?.data || error;
    }
  }

  public static async put(endpoint: string, params: any) {
    const token = LocalDataService.getAccessToken();
    const headers = {
      "Content-Type": "application/json",
      "X-API-KEY": xkey || "",
      Authorization: `${token}`,
    };

    try {
      const response = await client.put(`${baseUrl}${endpoint}`, params, { headers });
      return response.data;
    } catch (error: any) {
      console.warn(error);
      throw error.response?.data || error;
    }
  }

  public static async delete(endpoint: string, params: { [key: string]: any } | null) {
    const token = LocalDataService.getAccessToken();
    const url = params 
      ? `${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`
      : `${baseUrl}${endpoint}`;

    try {
      const response = await client.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": xkey || "",
          Authorization: `${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }
}